// Backend router: backend/routers/rule.py
import { useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { env } from '../utils/env';

// Cache for storing ETags
const etagCache = new Map<string, string>();

// Rule field and operator types based on the backend model
const RULE_FIELDS = [
    'merchant_name',
    'category',
    'account',
    'location',
    'amount',
    'recurring',
    'date',
    'transaction_type',
    'description',
] as const;

const RULE_OPERATORS = [
    'equals',
    'contains',
    'greater_than',
    'less_than',
    'between',
    'in_range',
] as const;

const RULE_TYPES = ['transaction', 'group_expense', 'shared'] as const;

const CONDITION_OPERATORS = ['and'] as const;

export type RuleField = typeof RULE_FIELDS[number];
export type RuleOperator = typeof RULE_OPERATORS[number];
export type RuleType = typeof RULE_TYPES[number];
export type ConditionOperator = typeof CONDITION_OPERATORS[number];

// Valid operators for each field type
const validOperators: Record<RuleField, readonly RuleOperator[]> = {
    merchant_name: ['equals', 'contains'] as const,
    description: ['equals', 'contains'] as const,
    location: ['equals', 'contains'] as const,
    category: ['equals'] as const,
    account: ['equals'] as const,
    amount: ['equals', 'greater_than', 'less_than', 'between'] as const,
    recurring: ['equals'] as const,
    date: ['equals', 'greater_than', 'less_than', 'in_range'] as const,
    transaction_type: ['equals'] as const,
};

// Condition interface for rule conditions
export interface RuleCondition {
    field: RuleField;
    operator: RuleOperator;
    value: string;
    negated?: boolean;
}

// Rule interface matching the backend model
export interface Rule {
    id: number;
    name: string;
    user_id: string;
    rule_type: RuleType;
    enabled: boolean;
    priority: number;

    // Conditions
    condition_operator: ConditionOperator;
    conditions: RuleCondition[];

    // Optional fields based on rule type
    group_id?: number;
    target_budget_category_id?: number;
    target_group_category_id?: number;
    split_type?: string;
    split_with?: Record<string, number>;
    split_details?: Record<string, any>;
    paid_by_id?: number;

    // Recurring rule fields
    recurrence_pattern?: string;
    recurrence_end_date?: string;
    next_occurrence?: string;

    // Rule time constraints
    start_date?: string;
    end_date?: string;

    // Metadata
    created_at: string;
    updated_at?: string;

    // For UI display
    budget_category?: {
        id: number;
        name: string;
    };
    group_category?: {
        id: number;
        name: string;
    };
}

// Rule creation params
export type RuleCreateParams = Omit<
    Rule,
    'id' | 'created_at' | 'updated_at' | 'user_id' | 'budget_category' | 'group_category'
> & {
    apply_to_past_transactions?: boolean;
};

// Rule update params
export type RuleUpdateParams = Partial<RuleCreateParams>;

// Helper function for creating a new condition
export const createEmptyCondition = (): RuleCondition => ({
    field: 'merchant_name',
    operator: 'equals',
    value: '',
    negated: false,
});

// Hook result interface
interface UseRulesResult {
    rules: Rule[];
    loading: boolean;
    error: string | null;
    createRule: (rule: RuleCreateParams) => Promise<Rule>;
    updateRule: (id: number, updates: RuleUpdateParams) => Promise<Rule>;
    deleteRule: (id: number) => Promise<boolean>;
    toggleRuleStatus: (id: number, enabled: boolean) => Promise<Rule>;
    applyRuleOnce: (transactionId: number, rule: RuleCreateParams) => Promise<{ matched: boolean; updated: boolean }>;
    applyGroupExpenseRuleOnce: (transactionId: number, rule: RuleCreateParams) => Promise<{ matched: boolean; updated: boolean }>;
    refresh: () => Promise<void>;
    refreshRules: () => Promise<void>;
    getOperatorsForField: (field: RuleField) => readonly RuleOperator[];
    createEmptyCondition: () => RuleCondition;
}

export const useRules = (
    accessToken: string,
    ruleType?: RuleType,
    groupId?: number,
    options: { isVisible?: boolean } = {}
): UseRulesResult => {
    const { isVisible = true } = options;
    const queryClient = useQueryClient();
    const initialFetchDone = useRef(false);

    // Determine the query key based on rule type and group ID
    const getQueryKey = () => {
        if (ruleType === 'group_expense' && groupId) {
            return ['rules', 'group_expense', groupId];
        } else if (ruleType === 'shared') {
            return ['rules', 'shared'];
        } else if (ruleType === 'transaction' || !ruleType) {
            return ['rules', 'transaction'];
        }
        // Default case
        return ['rules', ruleType, groupId];
    };

    const queryKey = getQueryKey();

    // Determine correct endpoint based on ruleType and groupId
    const getEndpoint = () => {
        if (ruleType === undefined || ruleType === null) {
            // If no ruleType is specified, use the endpoint to get all user rules
            if (groupId) {
                return `${env.API_URL}/api/rules/group/${groupId}`;
            } else {
                return `${env.API_URL}/api/rules/user`;
            }
        } else if (ruleType === 'group_expense') {
            if (!groupId) {
                throw new Error('groupId is required to fetch group expense rules');
            }
            return `${env.API_URL}/api/rules/group/${groupId}`;
        } else if (ruleType === 'shared') {
            return `${env.API_URL}/api/rules/shared`;
        } else if (ruleType === 'transaction') {
            return `${env.API_URL}/api/rules/transaction`;
        }
        // Default case
        return `${env.API_URL}/api/rules/user`;
    };

    // Main query to fetch rules
    const {
        data: rules = [],
        isLoading: loading,
        error: queryError,
        refetch
    } = useQuery({
        queryKey,
        queryFn: async (): Promise<Rule[]> => {
            if (!accessToken) {
                return [];
            }

            try {
                const endpoint = getEndpoint();
                const cacheKey = `rules_${ruleType || 'all'}_${groupId || 'none'}`;

                const headers: Record<string, string> = {
                    'Authorization': `Bearer ${accessToken}`,
                };

                // Add If-None-Match header if we have a cached ETag
                const cachedETag = etagCache.get(cacheKey);
                if (cachedETag) {
                    headers['If-None-Match'] = cachedETag;
                }

                const response = await fetch(endpoint, {
                    headers,
                    cache: 'no-cache', // Forces validation with server
                });

                // If we get a 304 Not Modified, use the cached data
                if (response.status === 304) {
                    console.log(`Using cached rules data for ${cacheKey} (304 Not Modified)`);
                    return queryClient.getQueryData<Rule[]>(queryKey) || [];
                }

                // Store the new ETag for future requests
                const newETag = response.headers.get('ETag');
                if (newETag) {
                    etagCache.set(cacheKey, newETag);
                }

                if (!response.ok) {
                    const errorText = await response.text();
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data = await response.json();
                // Ensure data is an array before returning
                if (Array.isArray(data)) {
                    return data;
                } else {
                    console.error('Received non-array data from rules API:', data);
                    throw new Error('Invalid data format received from rules API');
                }
            } catch (err) {
                console.error('Error fetching rules:', err);
                throw err;
            }
        },
        enabled: isVisible && !!accessToken,
        staleTime: 300000, // 5 minutes
    });

    // Convert React Query error to string format for API compatibility
    const error = queryError ? (queryError instanceof Error ? queryError.message : String(queryError)) : null;

    // Create rule mutation
    const createRuleMutation = useMutation({
        mutationFn: async (rule: RuleCreateParams): Promise<Rule> => {
            if (!accessToken) {
                throw new Error('Missing authentication');
            }

            // Make sure the rule has at least one condition
            if (!rule.conditions || rule.conditions.length === 0) {
                rule.conditions = [createEmptyCondition()];
            }

            // Use the correct endpoint based on rule type
            let url;
            if (rule.rule_type === 'group_expense' && rule.group_id) {
                url = `${env.API_URL}/api/rules/group/${rule.group_id}`;
            } else {
                url = `${env.API_URL}/api/rules/transaction`;
            }

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(rule),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
            }

            return await response.json();
        },
        onSuccess: (newRule) => {
            // Add the new rule to the cache
            queryClient.setQueryData(queryKey, (oldData: Rule[] = []) => [...oldData, newRule]);
        },
    });

    // Update rule mutation
    const updateRuleMutation = useMutation({
        mutationFn: async ({ id, updates }: { id: number, updates: RuleUpdateParams }): Promise<Rule> => {
            if (!accessToken) {
                throw new Error('Missing authentication');
            }

            // Find the rule to update (primarily to log or for context if needed, though endpoint doesn't strictly need it)
            const rule = rules.find(r => r.id === id);
            // if (!rule) {
            //   throw new Error(`Rule with ID ${id} not found`);
            // }

            // Consistently use the transaction rule update endpoint for all rule types
            // The backend service should differentiate based on the rule ID or properties if necessary.
            const url = `${env.API_URL}/api/rules/transaction/${id}`;

            const response = await fetch(url, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(updates),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
            }

            return await response.json();
        },
        onSuccess: (updatedRule) => {
            // Update the rule in the cache
            queryClient.setQueryData(queryKey, (oldData: Rule[] = []) =>
                oldData.map(rule => rule.id === updatedRule.id ? updatedRule : rule)
            );
        },
    });

    // Delete rule mutation
    const deleteRuleMutation = useMutation({
        mutationFn: async (id: number): Promise<boolean> => {
            if (!accessToken) {
                throw new Error('Missing authentication');
            }

            // Find the rule to delete
            const rule = rules.find(r => r.id === id);
            if (!rule) {
                console.warn(`Rule with ID ${id} not found, considering it already deleted`);
                return true;
            }

            // Use the correct endpoint based on rule type
            let url;
            if (rule.rule_type === 'group_expense' && rule.group_id) {
                url = `${env.API_URL}/api/rules/group/${rule.group_id}/${id}`;
            } else {
                url = `${env.API_URL}/api/rules/transaction/${id}`;
            }

            const response = await fetch(url, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            });

            // If the rule is not found (404), treat it as successfully deleted
            if (response.status === 404) {
                console.warn(`Rule ${id} not found on server during delete, likely already deleted.`);
                return true;
            }

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
            }

            return true;
        },
        onSuccess: (_, id) => {
            // Remove the rule from the cache
            queryClient.setQueryData(queryKey, (oldData: Rule[] = []) =>
                oldData.filter(rule => rule.id !== id)
            );
        },
    });

    // Apply rule once mutation
    const applyRuleOnceMutation = useMutation({
        mutationFn: async ({
            transactionId,
            ruleParams
        }: {
            transactionId: number,
            ruleParams: RuleCreateParams
        }): Promise<{ matched: boolean; updated: boolean }> => {
            if (!accessToken) throw new Error('Missing authentication');

            // Make sure the rule has at least one condition
            if (!ruleParams.conditions || ruleParams.conditions.length === 0) {
                ruleParams.conditions = [createEmptyCondition()];
            }

            const response = await fetch(`${env.API_URL}/api/rules/transaction/${transactionId}/apply-once`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(ruleParams),
            });

            if (!response.ok) {
                const errData = await response.json().catch(() => ({}));
                throw new Error(errData.detail || `HTTP ${response.status}`);
            }

            return response.json();
        },
    });

    // Apply group expense rule once mutation
    const applyGroupExpenseRuleOnceMutation = useMutation({
        mutationFn: async ({
            transactionId,
            ruleParams
        }: {
            transactionId: number,
            ruleParams: RuleCreateParams
        }): Promise<{ matched: boolean; updated: boolean }> => {
            if (!accessToken) throw new Error('Missing authentication');

            // Make sure the rule has at least one condition
            if (!ruleParams.conditions || ruleParams.conditions.length === 0) {
                ruleParams.conditions = [createEmptyCondition()];
            }

            const response = await fetch(`${env.API_URL}/api/rules/transaction/${transactionId}/apply-group-expense-rule-once`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(ruleParams),
            });

            if (!response.ok) {
                const errData = await response.json().catch(() => ({}));
                throw new Error(errData.detail || `HTTP ${response.status}`);
            }

            return response.json();
        },
    });

    // API compatibility wrapper functions
    const createRule = async (rule: RuleCreateParams): Promise<Rule> => {
        return createRuleMutation.mutateAsync(rule);
    };

    const updateRule = async (id: number, updates: RuleUpdateParams): Promise<Rule> => {
        return updateRuleMutation.mutateAsync({ id, updates });
    };

    const deleteRule = async (id: number): Promise<boolean> => {
        return deleteRuleMutation.mutateAsync(id);
    };

    const toggleRuleStatus = async (id: number, enabled: boolean): Promise<Rule> => {
        return updateRule(id, { enabled: !enabled });
    };

    const applyRuleOnce = async (
        transactionId: number,
        ruleParams: RuleCreateParams
    ): Promise<{ matched: boolean; updated: boolean }> => {
        return applyRuleOnceMutation.mutateAsync({ transactionId, ruleParams });
    };

    const applyGroupExpenseRuleOnce = async (
        transactionId: number,
        ruleParams: RuleCreateParams
    ): Promise<{ matched: boolean; updated: boolean }> => {
        return applyGroupExpenseRuleOnceMutation.mutateAsync({ transactionId, ruleParams });
    };

    const refresh = async (): Promise<void> => {
        await refetch();
    };

    const getOperatorsForField = (field: RuleField): readonly RuleOperator[] => {
        return validOperators[field] || [];
    };

    return {
        rules,
        loading,
        error,
        createRule,
        updateRule,
        deleteRule,
        toggleRuleStatus,
        applyRuleOnce,
        applyGroupExpenseRuleOnce,
        refresh,
        refreshRules: refresh,
        getOperatorsForField,
        createEmptyCondition,
    };
};

// Backend router: backend/routers/rule.py 